import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const POSTER_IMG =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_ideas-to-sales/artifacts/bycz2mct_ChatGPT%20Image%20Jul%2017%2C%202026%2C%2006_25_19%20PM.png";

const line1 = "Two engines.";
const line2 = "One revenue system.";

const containerV = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineV = {
    hidden: { y: "110%" },
    show: {
        y: "0%",
        transition: { duration: 1.05, ease: [0.19, 1, 0.22, 1] },
    },
};

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
    const rot = useTransform(scrollYProgress, [0, 1], [0, -1.2]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24 rule-bottom"
        >
            <div className="container-x relative z-10">
                {/* Meta line */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55, duration: 0.6 }}
                    className="flex items-center gap-3 mb-8"
                >
                    <span className="w-8 h-px bg-[#e84125]" />
                    <span
                        data-testid="hero-eyebrow"
                        className="font-mono uppercase text-[0.72rem] tracking-[0.28em] text-[#e84125]"
                    >
                        VDL · GTM Lead Gen · The Dual-Engine Advantage
                    </span>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
                    {/* Left: kinetic headline */}
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <motion.h1
                            data-testid="hero-headline"
                            variants={containerV}
                            initial="hidden"
                            animate="show"
                            className="font-display text-white text-[13vw] sm:text-[10vw] lg:text-[7.2vw] leading-[0.9] tracking-tighter uppercase balance max-w-[15ch]"
                        >
                            <span className="line-mask block">
                                <motion.span variants={lineV} className="block">
                                    {line1}
                                </motion.span>
                            </span>
                            <span className="line-mask block">
                                <motion.span variants={lineV} className="block">
                                    <span className="font-editorial italic font-normal text-[#e84125] normal-case tracking-normal">
                                        One&nbsp;
                                    </span>
                                    revenue
                                    <br className="hidden sm:block" />
                                    system.
                                </motion.span>
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.05, duration: 0.9 }}
                            data-testid="hero-subcopy"
                            className="mt-8 md:mt-10 text-white/70 text-base md:text-lg leading-relaxed font-body max-w-[46ch]"
                        >
                            A productized GTM engine for Payment Automation
                            SaaS. Inbound Meta &amp; Google. Outbound Clay,
                            LeadMagic, RB2B, Instantly. Wired into a single
                            feedback loop — engineered, measured, repeatable.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.25, duration: 0.9 }}
                            className="mt-8 flex flex-col sm:flex-row gap-4"
                        >
                            <a
                                href="#contact"
                                data-testid="hero-cta-primary"
                                className="btn-primary"
                            >
                                Book strategy call
                                <span aria-hidden>→</span>
                            </a>
                            <a
                                href="#manifesto"
                                data-testid="hero-cta-secondary"
                                className="btn-ghost"
                            >
                                Read the manifesto
                            </a>
                        </motion.div>
                    </div>

                    {/* Right: signature poster with spotlight + clipped frame + parallax */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
                        className="lg:col-span-5 order-1 lg:order-2 relative"
                    >
                        <div className="relative">
                            {/* Corner tickers */}
                            <div className="absolute -top-6 left-0 right-0 flex justify-between font-mono uppercase text-[0.6rem] tracking-[0.22em] text-white/40">
                                <span>Fig. 00 · Poster</span>
                                <span>30 / 30</span>
                            </div>
                            <motion.div
                                style={{ y, scale, rotate: rot }}
                                className="relative overflow-hidden border border-white/10 bg-[#0a0a0a] shadow-[0_40px_120px_-30px_rgba(232,65,37,0.35)]"
                            >
                                <img
                                    src={POSTER_IMG}
                                    alt="Viral Digital Labs — 30 Discovery Calls in 30 Days. GTM Engineering with Claude, Clay, Instantly, Apollo, Leadmagic, Origami, Sales Robot."
                                    className="w-full h-auto block"
                                    style={{
                                        filter: "contrast(1.03) saturate(1.05)",
                                    }}
                                    loading="eager"
                                    data-testid="hero-poster"
                                />
                                {/* Spotlight sweep */}
                                <div
                                    aria-hidden
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        background:
                                            "radial-gradient(ellipse 60% 55% at 30% 30%, rgba(255,255,255,0.10) 0%, transparent 55%)",
                                        mixBlendMode: "screen",
                                    }}
                                />
                                {/* Bottom vignette */}
                                <div
                                    aria-hidden
                                    className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, transparent 0%, rgba(5,5,5,0.65) 100%)",
                                    }}
                                />
                            </motion.div>
                            {/* Diagonal accent tab */}
                            <div className="absolute -bottom-3 -left-3 hidden sm:flex items-center gap-2 bg-[#e84125] text-[#050505] font-mono uppercase text-[0.62rem] tracking-[0.24em] px-3 py-2">
                                ● Min budget · Rs 1 Lac / mo
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Stat strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.55, duration: 0.8 }}
                    className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6 rule-top pt-10"
                >
                    {[
                        ["30", "Qualified meetings / month"],
                        ["90", "Days to steady-state pipeline"],
                        ["2×", "Engines. One clock."],
                        ["6–9 mo", "Payment SaaS cycles handled"],
                    ].map(([n, l]) => (
                        <div key={l} className="flex flex-col gap-2">
                            <span className="font-display text-white text-4xl md:text-5xl">
                                {n}
                            </span>
                            <span className="font-mono uppercase text-[0.68rem] tracking-[0.2em] text-white/50">
                                {l}
                            </span>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
